<template>
	<div>
		<PageHeader
			:data="{
				name: carnivore.name + ' (' + municipalityEntry.municipality.full_name + ')',
				breadcrumbs: [
					{ name: 'Home', to: '/' },
					{ name: 'Carnivores', to: '/carnivores' },
				],
			}"
			v-if="!finished"
		/>

		<div v-if="originalMunicipalityData" class="d-flex justify-content-end gap-2 mb-3">
			<button @click="keepChanges()" class="btn btn-sm btn-success">Keep Changes</button>
			<button @click="revertChanges()" class="btn btn-sm btn-warning">Revert Changes</button>
		</div>



		<div v-if="finished" class="row d-flex justify-content-center align-items-center text-center my-5">
			<div class="col-md-6 mt-5">
				<h1>🎉 Great Job - All entries for this carnivore campaign are done</h1>
				<h4>Thank you for doing great job! 👌</h4>
				<div class="mb-3">
					<span class="badge bg-success p-1 px-2 me-2">Finished: {{ userStats.finished }}</span>
					<span class="badge bg-warning p-1 px-2">Skipped: {{ userStats.skipped }}</span>
				</div>

				<h3 class="mt-4">Carnivore summary</h3>
				<h4>
					<span class="text-info">Total ({{ finishedStatus.total }})</span> | <span class="text-success">Finished ({{ finishedStatus.finished }})</span> |
					<span class="text-warning">Skipped ({{ finishedStatus.skipped }})</span>
				</h4>
			</div>
			<router-link to="/carnivores">Go back</router-link>
		</div>

		<div v-else class="row justify-content-center align-items-center">
			<div class="col-md-8">
				<div class="d-flex flex-column justify-content-center mb-3">
					<span class="">{{ progress.finished }} / {{ progress.total }}</span>
					<div class="progress" style="height: 25px;">
						<div
							class="progress-bar bg-success fs-6"
							role="progressbar"
							:style="'width:' + progress.percent + '%'"
							:aria-valuenow="progress.percent"
							aria-valuemin="0"
							aria-valuemax="100"
						>
							{{ progress.percent }} %
						</div>
					</div>
				</div>
			</div>
			<div class="col-md-4 mb-2">
				<div class="d-flex justify-content-end align-items-center">
					<div 
						class="d-inline-block me-2"
						data-bs-toggle="tooltip"
						data-bs-placement="top"
						:title="!(carnivore && carnivore.external_data && carnivore.external_data.data && carnivore.external_data.data.batch_run && carnivore.external_data.data.batch_run.status === 'completed') ? 'Batch run status: ' + ((carnivore && carnivore.external_data && carnivore.external_data.data && carnivore.external_data.data.batch_run && carnivore.external_data.data.batch_run.status) || 'unknown') : ''"
					>
						<button 
							v-if="carnivore && carnivore.external_data && carnivore.external_data.data && carnivore.external_data.data.batch_run" 
							@click="fillExtractedData()" 
							class="btn btn-info"
							:disabled="!(carnivore && carnivore.external_data && carnivore.external_data.data && carnivore.external_data.data.batch_run && carnivore.external_data.data.batch_run.status === 'completed')"
						>Fill Extracted Data</button>
					</div>
					<button 
						v-if="carnivore && carnivore.external_data && carnivore.external_data.data && carnivore.external_data.data.batch_run" 
						class="btn btn-outline-info btn-sm me-2"
						data-bs-toggle="modal"
						data-bs-target="#extractionDetailsModal"
					>
						<font-awesome-icon :icon="['fas', 'info-circle']" />
					</button>


					<button v-if="!loadingSkip" @click="skipMunicipality()" class="btn btn-warning me-2">Skip</button>
					<button v-else class="btn btn-primary" type="button" disabled>
						<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
						Skipping...
					</button>

					<button v-if="!loadingNext" @click="nextMunicipality()" class="btn btn-primary">Next</button>
					<button v-else class="btn btn-primary" type="button" disabled>
						<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
						Storing...
					</button>
				</div>
			</div>

			<!-- Fields to add -->
			<div v-if="carnivore.fields.length > 0">
				<div class="row mb-1">
					<!-- Municipality info data -->
					<div class="col-md-6">
						<div class="card">
							<div class="card-body p-2">
								<div class="d-flex justify-content-between align-items-center">
									<h6>Municipality {{ municipalityEntry.municipality.fips }}</h6>
									<h6>
										<a class="text-decoration-none" target="_blank" :href="'/municipalities/edit/' + municipalityEntry.municipality.id"
											>🏙️ {{ municipalityEntry.municipality.full_name }}</a
										>
										| {{ municipalityEntry.municipality.state }} | {{ municipalityEntry.municipality.county }} | 👪
										{{ municipalityEntry.municipality.population }}
									</h6>
								</div>

								<div class="row">
									<div class="col-md-4 mb-2">
										<div class="form-group">
											<label for="">Website</label>
											<input type="text" class="form-control form-control-sm" v-model="municipalityEntry.municipality.website" />
										</div>
									</div>
									<div class="col-md-4 mb-2" v-if="carnivore.fields.find(f => f.name === 'competitor').active">
										<div class="form-group">
											<label for="">TW Competitor</label>
											<input type="text" class="form-control form-control-sm" v-model="municipalityEntry.municipality.competitor" />
										</div>
									</div>
									<div class="col-md-4 mb-2" v-if="carnivore.fields.find(f => f.name === 'competitor_hg').active">
										<div class="form-group">
											<label for="">HG Competitor</label>
											<input type="text" class="form-control form-control-sm" v-model="municipalityEntry.municipality.competitor_hg" />
										</div>
									</div>
									<div class="col-md-4 mb-2" v-if="carnivore.fields.find(f => f.name === 'phone').active">
										<div class="form-group">
											<label for="">Phone</label>
											<input type="text" class="form-control form-control-sm" v-model="municipalityEntry.municipality.phone" />
										</div>
									</div>
									<div class="col-md-4 mb-2" v-if="carnivore.fields.find(f => f.name === 'email').active">
										<div class="form-group">
											<label for="">Email</label>
											<input type="text" class="form-control form-control-sm" v-model="municipalityEntry.municipality.email" />
										</div>
									</div>
									<div class="col-md-4 mb-2" v-if="carnivore.fields.find(f => f.name === 'population').active">
										<div class="form-group">
											<label for="">Population</label>
											<input type="text" class="form-control form-control-sm" v-model="municipalityEntry.municipality.population" />
										</div>
									</div>
									<div class="col-md-4 mb-2" v-if="carnivore.fields.find(f => f.name === 'county').active">
										<div class="form-group">
											<label for="">County</label>
											<input type="text" class="form-control form-control-sm" v-model="municipalityEntry.municipality.county" />
										</div>
									</div>
									<div class="col-md-4 mb-2" v-if="carnivore.fields.find(f => f.name === 'city').active">
										<div class="form-group">
											<label for="">City</label>
											<input type="text" class="form-control form-control-sm" v-model="municipalityEntry.municipality.city" />
										</div>
									</div>
									<div class="col-md-4 mb-2" v-if="carnivore.fields.find(f => f.name === 'zip').active">
										<div class="form-group">
											<label for="">ZIP</label>
											<input type="text" class="form-control form-control-sm" v-model="municipalityEntry.municipality.zip" />
										</div>
									</div>
									<div class="col-md-4 mb-2" v-if="carnivore.fields.find(f => f.name === 'address_street').active">
										<div class="form-group">
											<label for="">Address street|PO Box</label>
											<input type="text" class="form-control form-control-sm" v-model="municipalityEntry.municipality.address_street" />
										</div>
									</div>
									<!-- Features -->
									<div class="col-md-2 mb-2" v-if="carnivore.fields.find(f => f.name === 'issues').active">
										<div class="form-group">
											<label for="">Issues</label>
											<select class="form-select form-select-sm" v-model="municipalityEntry.municipality.issues">
												<option value="no">No</option>
												<option value="yes">Yes</option>
												<option value="maybe">Maybe</option>
											</select>
										</div>
									</div>

									<div class="col-md-2" v-if="carnivore.fields.find(f => f.name === 'boat_launch').active">
										<div class="form-group">
											<label for="">Boat launch</label>
											<select class="form-select form-select-sm" v-model="municipalityEntry.municipality.boat_launch">
												<option value="no">No</option>
												<option value="yes">Yes</option>
												<option value="maybe">Maybe</option>
											</select>
										</div>
									</div>

									<div class="col-md-2" v-if="carnivore.fields.find(f => f.name === 'park_rental').active">
										<div class="form-group">
											<label for="">Park rental</label>
											<select class="form-select form-select-sm" v-model="municipalityEntry.municipality.park_rental">
												<option value="no">No</option>
												<option value="yes">Yes</option>
												<option value="maybe">Maybe</option>
											</select>
										</div>
									</div>

									<div class="col-md-2" v-if="carnivore.fields.find(f => f.name === 'online_payments').active">
										<div class="form-group">
											<label for="">Online payments</label>
											<select class="form-select form-select-sm" v-model="municipalityEntry.municipality.online_payments">
												<option value="no">No</option>
												<option value="yes">Yes</option>
												<option value="maybe">Maybe</option>
											</select>
										</div>
									</div>

									<div class="col-md-2" v-if="carnivore.fields.find(f => f.name === 'fillable_forms').active">
										<div class="form-group">
											<label for="">Fillable forms</label>
											<select class="form-select form-select-sm" v-model="municipalityEntry.municipality.fillable_forms">
												<option value="no">No</option>
												<option value="yes">Yes</option>
												<option value="maybe">Maybe</option>
											</select>
										</div>
									</div>

									<div class="col-md-2" v-if="carnivore.fields.find(f => f.name === 'chat_box').active">
										<div class="form-group">
											<label for="">Chat box</label>
											<select class="form-select form-select-sm" v-model="municipalityEntry.municipality.chat_box">
												<option value="no">No</option>
												<option value="yes">Yes</option>
												<option value="maybe">Maybe</option>
											</select>
										</div>
									</div>

									<div class="col-md-2" v-if="carnivore.fields.find(f => f.name === 'alert_notifications').active">
										<div class="form-group">
											<label for="">Alert notifications</label>
											<select class="form-select form-select-sm" v-model="municipalityEntry.municipality.alert_notifications">
												<option value="no">No</option>
												<option value="yes">Yes</option>
												<option value="maybe">Maybe</option>
											</select>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<!-- Contacts -->
					<div class="col-md-6">
						<div class="card">
							<div class="card-body p-2">
								<div class="header d-flex justify-content-between align-items-start">
									<h6>Contacts</h6>
									<h6 @click="addNewContact()" class="text-primary fs-6 text-decoration-underline" role="button">
										+ Add new
									</h6>
								</div>

								<table class="table carnivore-contacts-table">
									<thead>
										<tr>
											<th>First Name</th>
											<th>Last name</th>
											<th>Email</th>
											<th>Phone</th>
											<th>Title</th>
											<th></th>
										</tr>
									</thead>
									<tbody v-if="municipalityEntry.contacts.length > 0">
										<tr v-for="(contact, index) in municipalityEntry.contacts" :key="contact.id">
											<td>
												<input type="text" class="form-control form-control-sm" v-model="contact.first_name" required />
											</td>
											<td>
												<input type="text" class="form-control form-control-sm" v-model="contact.last_name" />
											</td>
											<td>
												<input type="text" class="form-control form-control-sm" v-model="contact.email" />
											</td>
											<td>
												<input type="text" class="form-control form-control-sm" v-model="contact.phone" />
											</td>
											<td>
												<input type="text" class="form-control form-control-sm" v-model="contact.title" />
											</td>
											<td>
												<button @click="deleteContact(contact, index)" class="btn btn-danger btn-sm" v-if="contact.id">
													<font-awesome-icon :icon="['fas', 'trash']" class="text-white" />
												</button>
												<button @click="removeContact(index)" class="btn btn-warning btn-sm" v-if="!contact.id">
													<font-awesome-icon :icon="['fas', 'trash']" class="text-white" />
												</button>
											</td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr>
											<td colspan="5"><code>There is no contacts for this municipality</code></td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>

				<!-- Loading Iframe -->
				<div class="card">
					<div class="card-body p-2">
						<div class="toolbar mb-2">
							<div class="d-flex justify-content-between align-items-center">
								<div>
									<button class="btn btn-primary btn-sm" @click="setMunicipalityIframe()">
										<font-awesome-icon :icon="['fas', 'city']" class="text-white" />
										Website
									</button>
									<a :href="municipalityEntry.municipality.website" target="_blank" class="btn btn-sm btn-outline-primary me-3 border-0" title="Open in new tab">
										<font-awesome-icon :icon="['fas', 'link']" />
									</a>

									<button class="btn btn-primary btn-sm" @click="setWikipediaIframe()">
										<font-awesome-icon :icon="['fab', 'wikipedia-w']" class="me-1 text-white" />
										Wikipedia
									</button>
									<a :href="municipalityEntry.municipality.wikipedia" target="_blank" class="btn btn-sm btn-outline-primary me-3 border-0">
										<font-awesome-icon :icon="['fas', 'link']" />
									</a>
									<button class="btn btn-primary btn-sm" @click="setGoogleIframe()">
										<font-awesome-icon :icon="['fab', 'google']" class="me-1 text-white" /> Google
									</button>
									<a
										:href="'https://www.google.com/search?igu=1&q=' + municipalityEntry.municipality.full_name + ',' + municipalityEntry.municipality.state"
										target="_blank"
										class="btn btn-sm btn-outline-primary border-0"
									>
										<font-awesome-icon :icon="['fas', 'link']" />
									</a>
									<button class="btn btn-link btn-sm" data-bs-toggle="modal" data-bs-target="#municipalityUrlModal">
										<font-awesome-icon :icon="['fas', 'cog']" class="text-dark" />
									</button>
								</div>
								<div>
									<code v-if="!websiteFlag">{{ iframeUrl }}</code>
									<code v-else>
										<a :href="websiteUrl" target="_blank"><font-awesome-icon :icon="['fas', 'link']"/></a>
										{{ websiteUrl }}</code
									>
									<button v-if="websiteFlag" class="btn btn-link" @click="reloadWebsiteData(websiteUrl)">
										Reload
									</button>
								</div>
							</div>
						</div>
						<div
							v-if="!municipalityEntry.municipality.website"
							class="alert alert-danger d-flex align-items-center justify-content-center text-white p-1 ps-3 text-center"
							role="alert"
						>
							<div class="text-center">
								This municipality missing website, you can add it on gear icon
							</div>
						</div>

						<div
							v-if="municipalityEntry.municipality.website && !municipalityEntry.municipality.website.includes('http')"
							class="alert alert-warning d-flex align-items-center justify-content-center text-white p-1 ps-3 text-center"
							role="alert"
						>
							<div class="text-center">
								This municipality website missing http:// or https:// click on one button to add it
								<button class="btn btn-primary btn-sm text-white fw-normal" @click="setHelpUrl('http://')" role="button">
									http://
								</button>
								or
								<button class="btn btn-primary btn-sm text-white fw-normal" @click="setHelpUrl('https://')" role="button">
									https://
								</button>
							</div>
						</div>

				<div v-if="notValidWebsite" class="alert alert-warning d-flex align-items-center justify-content-center text-white p-1 ps-3 text-center" role="alert">
					<div class="text-center">
						It seems that the website address is not correct, please check and correct it by clicking on gear icon or
						<button class="btn btn-primary btn-sm text-white fw-normal" data-bs-toggle="modal" data-bs-target="#municipalityUrlModal" role="button">
							here
						</button>
					</div>
				</div>

				<div
					class="alert alert-danger d-flex justify-content-between align-items-center p-2"
					v-if="this.municipalityEntry.municipality.website === iframeUrl && !websiteFlag"
					style="position:absolute"
				>
					<div class="d-block justify-content-start align-items-center">
						Website is broken?
						<button class="btn btn-sm btn-primary ms-2" @click="showIframeFromAPI()">
							Force load
						</button>
					</div>
				</div>

				<!-- Load over default Iframe -->
				<div v-if="!websiteFlag" style="height:1000px" class="normal-iframe w-100" v-html="iframeHTML"></div>

						<div class="fetch-frame" v-else>
							<code v-if="false">Website is loaded over API</code>
							<vue-iframe
								v-if="!websiteContentLoading"
								:src="websiteData"
								allow="camera *; geolocation *; microphone *; autoplay *"
								frame-id="iframeWebsite"
								@load="onWebsiteLoad"
								name="my-frame"
								width="150px"
								height="200px"
							/>
							<div v-if="websiteContentLoading" class="d-flex justify-content-center">
								<div class="spinner-border" role="status">
									<span class="visually-hidden">Loading...</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for url-s -->
		<div class="modal fade" id="municipalityUrlModal" tabindex="-1" aria-labelledby="createLabel" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-body">
						<h5>Municipality sources Url-s</h5>
						<div class="row">
							<div class="col-md-12">
								<div class="form-group">
									<label for="">Website URL</label>
									<input type="text" v-model="municipalityEntry.municipality.website" class="form-control" />
								</div>
								<div class="form-group">
									<label for="">Wikipedia URL</label>
									<input type="text" v-model="municipalityEntry.municipality.wikipedia" class="form-control" />
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-info" @click="updateUrls()" data-bs-dismiss="modal">
							Save
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Extraction Details Modal -->
		<div class="modal fade" id="extractionDetailsModal" tabindex="-1" aria-labelledby="extractionDetailsLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="extractionDetailsLabel">Extraction Details</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div v-if="getCurrentExtractionData()">
							<div class="mb-3">
								<h6>Extracted Data</h6>
								<pre class="bg-light p-3 rounded">{{ JSON.stringify(getCurrentExtractionData().extracted_data, null, 2) }}</pre>
							</div>
							<div class="mb-3">
								<h6>Search Context</h6>
								<div class="bg-light p-3 rounded" style="max-height: 200px; overflow-y: auto;">
									<pre class="mb-0">{{ getCurrentExtractionData().meilisearch_context }}</pre>
								</div>
							</div>
							<div class="row">
								<div class="col-md-6">
									<p><strong>Tokens Used:</strong> {{ getCurrentExtractionData().tokens_used }}</p>
								</div>
								<div class="col-md-6">
									<p><strong>Cost:</strong> ${{ getCurrentExtractionData().cost_usd }}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import { mapState } from 'vuex'
import restApi from '../../api/index.js'
import { handleErrors } from '../../actions/helpers'
import { Modal, Tooltip } from 'bootstrap'

export default {
	name: 'CarnivoreWork',
	components: { PageHeader },
	data() {
		return {
			notValidWebsite: false,
			websiteContentLoading: false,
			websiteFlag: false,
			loading: false,
			loadingNext: false,
			loadingSkip: false,
			originalMunicipalityData: null,
			updatedFields: [],
			showOldValues: false,
			carnivore: {
				name: '',
				fields: [],
			},
			iframeUrl: '',
			$userPersonalSettings: null,
			personalSettings: {
				iframeWidth: '100%',
				iframeWidthUnit: 'px',
				iframeHeight: '1000',
				iframeHeightUnit: 'px',
			},
			municipalityEntry: {
				municipality: {
					ful_name: '',
				},
			},
			clients: [],
			finished: false,
			finishedStatus: null,
			progress: {
				total: 0,
				finished: 0,
				percent: 0,
			},
			websiteData: '',
			websiteLinks: null,
			websiteLoading: false,
			websiteUrl: '',
			userStats: {
				finished: 0,
				skipped: 0,
			},
			helpUrl: '',
			$iframe: null,
			iframeHTML: '',
		}
	},
	computed: {
		...mapState(['authUser', 'storageUrl']),
	},
	created() {
		this.getStartEntry()
		this.getCarnivore()
		this.carnivoreProgress()
		
		const timer = setInterval(() => {
			this.carnivoreProgress()
		}, 30000)

		this.$once('hook:beforeDestroy', () => {
			clearInterval(timer)
		})
	},

	methods: {



		// Show iframe with response data from API
		showIframeFromAPI() {
			this.websiteUrl = this.municipalityEntry.municipality.website
			this.websiteFlag = true
			this.websiteLoading = true
			this.getWebsiteContent()
		},

		reloadWebsiteData(website) {
			this.websiteContentLoading = true
			return new Promise((resolve, reject) => {
				restApi
					.post(`/helper/website-content`, { website: website })
					.then(({ data }) => {
						this.websiteContentLoading = false
						this.websiteData = data
						if (!data && !website.includes('https://')) {
							website = website.replace('http', 'https')
							this.reloadWebsiteData(website)
						}
						//document.getElementById('iframeWebsite').reload(true)
						resolve(data)
					})
					.catch(error => {
						reject()
						this.$swal.fire({
							icon: 'error',
							title: handleErrors(error, ''),
							timer: 4000,
						})
					})
			})
		},

		// Fill extracted data from external source
		fillExtractedData() {
			// Store original data for potential revert
			this.originalMunicipalityData = JSON.parse(JSON.stringify(this.municipalityEntry));
			this.updatedFields = [];

			// Check if external data exists before accessing
			if (!(this.carnivore && this.carnivore.external_data && this.carnivore.external_data.data && this.carnivore.external_data.data.results)) {
				return;
			}

			// Find matching result by website URL
			const matchingResult = this.carnivore.external_data.data.results.find(
				result => result.website.url === this.municipalityEntry.municipality.website
			);

			if (!matchingResult) {
				this.$swal.fire({
					icon: 'error',
					title: 'No matching extracted data found for this website',
					timer: 4000,
				});
				return;
			}

			const extractedData = matchingResult.extracted_data;

			// Update municipality fields
			const municipalityFields = ['email', 'phone', 'county', 'city', 'zip', 'address_street'];
			municipalityFields.forEach(field => {
				if (extractedData[field] && extractedData[field] !== this.municipalityEntry.municipality[field]) {
					this.municipalityEntry.municipality[field] = extractedData[field];
					this.updatedFields.push(`municipality.${field}`);
				}
			});

			// Update contacts
			if (extractedData.contacts) {
				Object.entries(extractedData.contacts).forEach(([contactType, contactData]) => {
					if (contactData.firstName || contactData.lastName || contactData.email || contactData.phone || contactData.title) {
						// Find existing contact of this type or create new one
						let contact = this.municipalityEntry.contacts.find(c => c.type === contactType);
						if (!contact) {
							contact = {
								first_name: '',
								last_name: '',
								email: '',
								phone: '',
								title: '',
								type: contactType,
								source: 'MC2-Carnivore',
								status: 'active',
								municipality_id: this.municipalityEntry.municipality_id
							};
							this.municipalityEntry.contacts.push(contact);
						}

						// Update contact fields
						if (contactData.firstName && contactData.firstName !== contact.first_name) {
							contact.first_name = contactData.firstName;
							this.updatedFields.push(`contacts.${contactType}.first_name`);
						}
						if (contactData.lastName && contactData.lastName !== contact.last_name) {
							contact.last_name = contactData.lastName;
							this.updatedFields.push(`contacts.${contactType}.last_name`);
						}
						if (contactData.email && contactData.email !== contact.email) {
							contact.email = contactData.email;
							this.updatedFields.push(`contacts.${contactType}.email`);
						}
						if (contactData.phone && contactData.phone !== contact.phone) {
							contact.phone = contactData.phone;
							this.updatedFields.push(`contacts.${contactType}.phone`);
						}
						if (contactData.title && contactData.title !== contact.title) {
							contact.title = contactData.title;
							this.updatedFields.push(`contacts.${contactType}.title`);
						}
					}
				});
			}

			if (this.updatedFields.length === 0) {
				this.$swal.fire({
					icon: 'info',
					title: 'No new data to update',
					timer: 4000,
				});
			}
		},

		// Keep the changes
		keepChanges() {
			this.updatedFields = [];
		},

		// Revert the changes
		revertChanges() {
			this.municipalityEntry = JSON.parse(JSON.stringify(this.originalMunicipalityData));
			this.updatedFields = [];
		},

		// Get Start Carnivore Municipality Entry
		getStartEntry() {
			restApi
				.post(`/carnivores/${this.$route.params.id}/start`)
				.then(({ data }) => {
					if (data.finished) {
						this.getUserFinishedStatus()
						this.finished = true
						this.finishedStatus = data
					} else {
						this.municipalityEntry = data
						if (!this.isValidUrl(this.municipalityEntry?.municipality?.website)) {
							this.notValidWebsite = true
						} else {
							this.iframeUrl = this.municipalityEntry?.municipality?.website
							this.iframeHTML = ''
							this.iframeHTML = `<iframe src="${this.iframeUrl}" id="iframe" width="${this.personalSettings.iframeWidth}" height="${this.personalSettings.iframeHeight}" frameborder="0" target="_parent"></iframe>`
						}
					}
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Get status of finished carnivore for user
		getUserFinishedStatus() {
			restApi
				.post(`/carnivores/${this.$route.params.id}/user-finished-stats`)
				.then(({ data }) => {
					this.userStats = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Get Carnivore
		getCarnivore() {
			restApi
				.get(`/carnivores/${this.$route.params.id}`)
				.then(({ data }) => {
					this.carnivore = data
					let fields = JSON.parse(this.carnivore.fields)
					this.carnivore.fields = fields
					let filters = JSON.parse(this.carnivore.filters)
					this.carnivore.filters = filters
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Add new contact
		addNewContact() {
			let newContact = {
				first_name: '',
				last_name: '',
				name: '',
				phone: '',
				source: 'MC2-Carnivore',
				title: '',
				status: 'active',
				municipality_id: this.municipalityEntry.municipality_id,
			}
			this.municipalityEntry.contacts.push(newContact)
		},

		// Remove contact from array
		removeContact(index) {
			this.municipalityEntry.contacts.splice(index, 1)
		},

		deleteContact(contact, index) {
			this.$swal
				.fire({
					title: 'Are you sure?',
					text: "You won't be able to revert this!",
					icon: 'warning',
					position: 'center',
					showCancelButton: true,
					showConfirmButton: true,
					confirmButtonColor: '#3085d6',
					cancelButtonColor: '#d33',
					confirmButtonText: 'Yes, delete it!',
					toast: false,
					timer: 0,
				})
				.then(result => {
					if (result.isConfirmed) {
						restApi
							.delete(`/contacts/${contact.id}`)
							.then(() => {
								this.municipalityEntry.contacts.splice(index, 1)
							})
							.catch(error => {
								this.$swal.fire({
									icon: 'error',
									title: handleErrors(error, ''),
									timer: 6000,
								})
							})
					}
				})
		},

		// Get next municipality
		nextMunicipality() {
			this.notValidWebsite = false;
			this.websiteFlag = false;
			this.iframeUrl = '';
			this.iframeHTML = '';
			this.loadingNext = true;
			this.websiteData = '';
			this.websiteFlag = false;
			this.originalMunicipalityData = null; // Clear original data when moving to next
			let allFine = this.checkContactData()

			if (this.municipalityEntry.municipality.website && !this.municipalityEntry.municipality.website.includes('http')) {
				this.municipalityEntry.municipality.website = this.helpUrl + this.municipalityEntry.municipality.website
			}

			if (allFine) {
				restApi
					.post(`/carnivores/${this.$route.params.id}/next`, {
						current: this.municipalityEntry.id,
						municipalityEntryReq: this.municipalityEntry,
					})
					.then(({ data }) => {
						if (data.finished) {
							this.getUserFinishedStatus()
							this.finished = true
							this.finishedStatus = data
						} else {
							this.municipalityEntry = data
							if (this.isValidUrl(this.municipalityEntry?.municipality?.website)) {
								this.iframeUrl = this.municipalityEntry?.municipality?.website
								this.notValidWebsite = false
							}
							this.iframeHTML = ''
							this.iframeHTML = `<iframe src="${this.iframeUrl}" id="iframe" width="${this.personalSettings.iframeWidth}" height="${this.personalSettings.iframeHeight}" frameborder="0" target="_parent"></iframe>`
							this.loadingNext = false
							this.carnivoreProgress()
						}
					})
					.catch(error => {
						this.$swal.fire({
							icon: 'error',
							title: handleErrors(error, ''),
							timer: 6000,
						})
					})
			} else {
				this.$swal.fire({
					icon: 'error',
					title: 'Contact data is missing!',
					timer: 6000,
				})
				this.loadingNext = false
			}
		},

		checkContactData() {
			let contactData = true
			this.municipalityEntry.contacts.forEach(contact => {
				if (contact.first_name === '' || contact.last_name === '' || contact.email === '' || contact.title === '') {
					contactData = false
				}
			})
			return contactData
		},

		skipMunicipality() {
			this.notValidWebsite = false
			this.websiteFlag = false
			this.iframeUrl = ''
			this.iframeHTML = ''
			this.loadingSkip = true
			restApi
				.post(`/carnivores/${this.$route.params.id}/next`, {
					current: this.municipalityEntry.id,
					municipalityEntryReq: this.municipalityEntry,
					skipped: true,
				})
				.then(({ data }) => {
					this.municipalityEntry = data
					if (!this.isValidUrl(this.municipalityEntry?.municipality?.website)) {
						this.notValidWebsite = true
					} else {
						this.iframeUrl = this.municipalityEntry?.municipality?.website
						this.iframeHTML = ''
						this.iframeHTML = `<iframe src="${this.iframeUrl}" id="iframe" width="${this.personalSettings.iframeWidth}" height="${this.personalSettings.iframeHeight}" frameborder="0" target="_parent"></iframe>`
					}
					this.loadingSkip = false
					this.this.carnivoreProgress()
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		carnivoreProgress() {
			restApi
				.post(`/carnivores/${this.$route.params.id}/progress`)
				.then(({ data }) => {
					this.progress = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Get website content
		getWebsiteContent() {
			this.websiteContentLoading = true
			return new Promise((resolve, reject) => {
				restApi
					.post(`/helper/website-content`, { website: this.municipalityEntry.municipality.website })
					.then(({ data }) => {
						this.websiteContentLoading = false
						this.websiteData = data
						resolve(data)
					})
					.catch(error => {
						reject()
						this.websiteContentLoading = false
						this.websiteData = ''
						this.websiteFlag = false
						this.$swal.fire({
							icon: 'error',
							title: handleErrors(error, ''),
							timer: 4000,
						})
					})
			})
		},

		// Help url https or http
		setHelpUrl(url) {
			this.helpUrl = url
			this.municipalityEntry.municipality.website = url + this.municipalityEntry?.municipality?.website
			if (this.isValidUrl(this.municipalityEntry?.municipality?.website)) {
				this.iframeUrl = this.municipalityEntry?.municipality?.website
				this.notValidWebsite = false
			}
			this.iframeHTML = ''
			this.iframeHTML = `<iframe src="${this.iframeUrl}" id="iframe" width="${this.personalSettings.iframeWidth}" height="${this.personalSettings.iframeHeight}" frameborder="0" target="_parent"></iframe>`
		},

		// Reload website content

		// Update links
		updateLinks() {
			const iframe = document.getElementById('iframeWebsite')
			window.test = this
			const iWindow = iframe.contentWindow
			const iDocument = iWindow.document
			// Accessing the element
			const linkElements = iDocument.getElementsByTagName('a')
			for (let link of linkElements) {
				link.classList.add('sk-maestro')
				link.addEventListener('click', function handleClick() {
					window.test.getWebsitePageContent(link.href)
				})
			}
			this.websiteLoading = false
		},

		// Get new page created with by you
		getWebsitePageContent(url) {
			let newPage = url.replace(window.location.origin, this.municipalityEntry.municipality.website)
			this.websiteUrl = newPage
			restApi
				.post(`/helper/website-content`, { website: newPage })
				.then(({ data }) => {
					this.websiteData = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 4000,
					})
				})
		},

		// When iframe is loaded
		onWebsiteLoad() {
			this.updateLinks()
		},

		// When change website url reload to have new one
		updateUrls() {
			if (this.isValidUrl(this.municipalityEntry?.municipality?.website)) {
				this.iframeUrl = this.municipalityEntry?.municipality?.website
				this.notValidWebsite = false
			}
			this.iframeHTML = ''
			this.iframeHTML = `<iframe src="${this.iframeUrl}" id="iframe" width="${this.personalSettings.iframeWidth}" height="${this.personalSettings.iframeHeight}" frameborder="0" target="_parent"></iframe>`
		},

		setGoogleIframe() {
			this.websiteFlag = false
			this.iframeUrl = 'https://www.google.com/search?igu=1&q=' + this.municipalityEntry.municipality?.full_name + ',' + this.municipalityEntry.municipality?.state
			this.notValidWebsite = false
			this.iframeHTML = ''
			this.iframeHTML = `<iframe src="${this.iframeUrl}" id="iframe" width="${this.personalSettings.iframeWidth}" height="${this.personalSettings.iframeHeight}" frameborder="0" target="_parent"></iframe>`
		},
		setMunicipalityIframe() {
			this.websiteFlag = false
			if (this.isValidUrl(this.municipalityEntry?.municipality?.website)) {
				this.iframeUrl = this.municipalityEntry?.municipality?.website
				this.notValidWebsite = false
				this.iframeHTML = `<iframe src="${this.iframeUrl}" id="iframe" width="${this.personalSettings.iframeWidth}" height="${this.personalSettings.iframeHeight}" frameborder="0" target="_parent"></iframe>`
			} else {
				this.iframeHTML = ''
				this.notValidWebsite = true
				this.iframeUrl = ''
				this.iframeHTML = `<iframe src="${this.iframeUrl}" id="iframe" width="${this.personalSettings.iframeWidth}" height="${this.personalSettings.iframeHeight}" frameborder="0" target="_parent"></iframe>`
			}
		},
		setWikipediaIframe() {
			this.websiteFlag = false
			this.iframeUrl = this.municipalityEntry.municipality?.wikipedia
			this.notValidWebsite = false
			this.iframeHTML = ''
			this.iframeHTML = `<iframe src="${this.iframeUrl}" id="iframe" width="${this.personalSettings.iframeWidth}" height="${this.personalSettings.iframeHeight}" frameborder="0" target="_parent"></iframe>`
		},
		isValidUrl(urlString) {
			var urlPattern = new RegExp(
				'^(https?:\\/\\/)?' + // validate protocol
				'((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // validate domain name
				'((\\d{1,3}\\.){3}\\d{1,3}))' + // validate OR ip (v4) address
				'(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // validate port and path
				'(\\?[;&a-z\\d%_.~+=-]*)?' + // validate query string
					'(\\#[-a-z\\d_]*)?$',
				'i'
			) // validate fragment locator
			return !!urlPattern.test(urlString)
		},
		// Get the old value for a field
		getOldValue(fieldPath) {
			const parts = fieldPath.split('.');
			let value = this.originalMunicipalityData;
			for (const part of parts) {
				if (value && value[part] !== undefined) {
					value = value[part];
				} else {
					return '';
				}
			}
			return value || '';
		},

		// Get the new value for a field
		getNewValue(fieldPath) {
			const parts = fieldPath.split('.');
			let value = this.municipalityEntry;
			for (const part of parts) {
				if (value && value[part] !== undefined) {
					value = value[part];
				} else {
					return '';
				}
			}
			return value || '';
		},

		// Get current extraction data
		getCurrentExtractionData() {
			if (!(this.carnivore && this.carnivore.external_data && this.carnivore.external_data.data && this.carnivore.external_data.data.results)) return null;
			return this.carnivore.external_data.data.results.find(
				result => result.website.url === this.municipalityEntry.municipality.website
			);
		},

		// Initialize tooltips
		initializeTooltips() {
			document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach(el => {
				// Destroy existing tooltip if it exists
				const existingTooltip = Tooltip.getInstance(el)
				if (existingTooltip) {
					existingTooltip.dispose()
				}
				// Create new tooltip
				new Tooltip(el, {
					trigger: 'hover'
				})
			})
		},
	},
	mounted: function() {
		this.$userPersonalSettings = new Modal('#municipalityUrlModal')
		this.$iframe = document.getElementById('iframe')
		window.test = this
		
		// Initialize tooltips
		this.initializeTooltips()
	},
	watch: {
		'carnivore.external_data': {
			handler() {
				// Reinitialize tooltips when external data changes
				this.$nextTick(() => {
					this.initializeTooltips()
				})
			},
			deep: true
		}
	},
}
</script>

<style lang="scss">
.vue-iframe {
	iframe {
		width: 100% !important;
		height: 1000px !important;
		visibility: visible !important;
		top: 0 !important;
		position: relative !important;
	}
}

.test {
	.vue-iframe {
		iframe {
			width: 100% !important;
			height: 1000px !important;
			visibility: visible !important;
			top: 0 !important;
			position: relative !important;
		}
	}
}
</style>
